import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import Modal, { Field, TextInput, TextArea, Select, PrimaryButton, SecondaryButton } from "@/components/FormKit";
import { toast } from "sonner";
import { fmtDateTime, relativeTime } from "@/lib/format";

const initial = { project_id: "", title: "", body: "", milestone: "", images: "", progress: "" };

export default function Updates() {
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initial);

  const load = async () => {
    const [u, p] = await Promise.all([api.get("/company/updates"), api.get("/company/projects")]);
    setItems(u.data);
    setProjects(p.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/company/updates", {
        project_id: form.project_id,
        title: form.title,
        body: form.body,
        milestone: form.milestone || null,
        progress: form.progress ? Number(form.progress) : null,
        images: form.images.split(/\s*,\s*/).filter(Boolean),
      });
      toast.success("Update published · customers notified");
      setOpen(false);
      setForm(initial);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-8" data-testid="company-updates-page">
      <PageHeader
        eyebrow="Feed"
        title="Construction updates"
        description="Publish progress updates with images, milestones, and progress %."
        actions={<PrimaryButton onClick={() => setOpen(true)} testId="new-update-button">+ Publish Update</PrimaryButton>}
      />

      <div className="timeline">
        {items.map((u) => (
          <article key={u.id} className="relative pb-8" data-testid={`update-${u.id}`}>
            <div className="timeline-dot" />
            <div className="grid-surface">
              {u.images && u.images.length > 0 && (
                <div className="grid grid-cols-2 gap-0.5 bg-stone-200">
                  {u.images.slice(0, 2).map((src, i) => (
                    <img key={i} src={src} alt="" className="w-full h-48 object-cover" />
                  ))}
                </div>
              )}
              <div className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="overline text-brand-600">
                      {u.project?.name} {u.milestone && `· ${u.milestone}`}
                    </div>
                    <h3 className="font-display text-xl font-bold mt-1">{u.title}</h3>
                  </div>
                  <div className="text-xs text-stone-500 font-mono whitespace-nowrap">
                    {relativeTime(u.created_at)}
                  </div>
                </div>
                <p className="text-stone-700 mt-3 leading-relaxed">{u.body}</p>
                <div className="mt-3 text-xs text-stone-500 font-mono border-t border-stone-100 pt-2">
                  {u.author_name && <span>By {u.author_name} · </span>}
                  {fmtDateTime(u.created_at)}
                  {u.progress != null && <span> · {u.progress}% project progress</span>}
                </div>
              </div>
            </div>
          </article>
        ))}
        {items.length === 0 && (
          <div className="grid-surface border-dashed p-10 text-center overline text-stone-400">
            No updates yet. Publish your first one.
          </div>
        )}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="Publish construction update">
        <form onSubmit={submit}>
          <Field label="Project">
            <Select testId="form-upd-project" required value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })}>
              <option value="">Select project…</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
          </Field>
          <Field label="Title"><TextInput testId="form-upd-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
          <Field label="Body"><TextArea testId="form-upd-body" rows={4} required value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Milestone (optional)"><TextInput testId="form-upd-milestone" placeholder="e.g. Foundation" value={form.milestone} onChange={(e) => setForm({ ...form, milestone: e.target.value })} /></Field>
            <Field label="Progress %"><TextInput testId="form-upd-progress" type="number" min="0" max="100" value={form.progress} onChange={(e) => setForm({ ...form, progress: e.target.value })} /></Field>
          </div>
          <Field label="Image URLs (comma separated)">
            <TextInput testId="form-upd-images" value={form.images} onChange={(e) => setForm({ ...form, images: e.target.value })} placeholder="https://…, https://…" />
          </Field>
          <div className="flex justify-end gap-2 pt-2">
            <SecondaryButton type="button" onClick={() => setOpen(false)}>Cancel</SecondaryButton>
            <PrimaryButton type="submit" testId="submit-update">Publish</PrimaryButton>
          </div>
        </form>
      </Modal>
    </div>
  );
}
